import type React from "react";
import { forwardRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
} from "@mui/material";
import { LockClosedIcon } from "@heroicons/react/outline";
import LanguageIcon from "src/common/components/Icon/LanguageIcon";
import { useTranslation } from "react-i18next";
import type { GenericTableRow } from "src/common/components/ModernGenericTable/types";
import { renderTableCellValue } from "src/common/components/ModernGenericTable/types";
import { useGenericTableState } from "src/common/components/ModernGenericTable/useGenericTableState";
import SortIndicator from "src/common/components/ModernGenericTable/SortIndicator";
import TableSearchField from "src/common/components/ModernGenericTable/TableSearchField";

export interface LanguageSelectionTableProps<Row extends GenericTableRow = GenericTableRow> {
  maxTableHeightPx?: number;
  rows: Row[];
  colHeaders: string[];
  className?: string;
  cellClassName?: string;
  emptyPlaceholder?: React.ReactNode;
  checkAll?: boolean;
  showSearchBar?: boolean;
  filterActivated?: boolean;
  onSelectionChange?: (selectedRowIds: (string | number)[]) => void;
  customSortFunctions?: {
    [key: string]: (a: Row, b: Row, direction: "asc" | "desc") => number;
  };
  selectedRowIds: (string | number)[];
  nonDeselectableRowIds?: (string | number)[];
  customCellRenderers?: {
    [columnHeader: string]: (row: Row) => React.ReactNode;
  };
  columnAlignments?: { [columnHeader: string]: "left" | "center" | "right" };
  showCheckboxesForUnselected?: boolean;
}

type LanguageSelectionTableComponent = (<Row extends GenericTableRow = GenericTableRow>(
  props: LanguageSelectionTableProps<Row> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement | null) & { displayName?: string };

const applyLockedFirstOrder = <Row extends GenericTableRow = GenericTableRow>(
  rows: Row[],
  selectedRowIds: (string | number)[],
  nonDeselectableRowIds?: (string | number)[],
): Row[] => {
  const lockedRows = nonDeselectableRowIds
    ? rows.filter((row) => nonDeselectableRowIds.includes(row.id))
    : [];
  const nonLockedRows = nonDeselectableRowIds
    ? rows.filter((row) => !nonDeselectableRowIds.includes(row.id))
    : rows;
  const nonLockedSelected = nonLockedRows.filter((row) => selectedRowIds.includes(row.id));
  const nonLockedUnselected = nonLockedRows.filter((row) => !selectedRowIds.includes(row.id));
  return [...lockedRows, ...nonLockedSelected, ...nonLockedUnselected];
};

const LanguageSelectionTable = forwardRef(function LanguageSelectionTable<
  Row extends GenericTableRow = GenericTableRow,
>(
  {
    maxTableHeightPx = 0,
    rows,
    colHeaders,
    className = "",
    cellClassName = "",
    emptyPlaceholder = "No data available",
    checkAll = false,
    showSearchBar = false,
    filterActivated = true,
    onSelectionChange = () => {},
    customSortFunctions = {},
    selectedRowIds,
    nonDeselectableRowIds,
    customCellRenderers,
    columnAlignments,
    showCheckboxesForUnselected = true,
  }: LanguageSelectionTableProps<Row>,
  ref: React.Ref<HTMLDivElement>,
) {
  const { t } = useTranslation();
  const {
    searchString,
    sortedColumn,
    sortDirection,
    displayRows,
    handleSearch,
    handleSort,
    handleRowCheckboxChange,
    handleCheckAllChange,
  } = useGenericTableState<Row>({
    rows,
    colHeaders,
    customSortFunctions,
    selectedRowIds,
    onSelectionChange,
    nonDeselectableRowIds,
  });

  const headerHeight = 56;

  const orderedRows = applyLockedFirstOrder(displayRows, selectedRowIds, nonDeselectableRowIds);

  const rowsToRender =
    showCheckboxesForUnselected || searchString
      ? orderedRows
      : orderedRows.filter(
          (row) =>
            selectedRowIds.includes(row.id) ||
            (nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id)),
        );

  const selectableRows = orderedRows.filter(
    (row) => !nonDeselectableRowIds || !nonDeselectableRowIds.includes(row.id),
  );

  const headerCellStyle: React.CSSProperties = {
    padding: "0 8px",
  };

  const renderHeader = () => (
    <Table style={{ tableLayout: "fixed", width: "100%" }}>
      <TableHead>
        <TableRow style={{ height: headerHeight }}>
          {checkAll && (
            <TableCell
              style={{
                ...headerCellStyle,
                width: 50,
                textAlign: "center",
              }}
            >
              {!searchString && (
                <Checkbox
                  size="small"
                  color="default"
                  sx={{
                    color: "gray",
                    "&.Mui-checked": { color: "#00C889" },
                  }}
                  indeterminate={
                    selectableRows.length > 0 &&
                    !selectableRows.every((row) => selectedRowIds.includes(row.id)) &&
                    selectableRows.some((row) => selectedRowIds.includes(row.id))
                  }
                  checked={
                    selectableRows.length > 0 &&
                    selectableRows.every((row) => selectedRowIds.includes(row.id))
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCheckAllChange();
                  }}
                />
              )}
            </TableCell>
          )}

          <TableCell
            style={{
              ...headerCellStyle,
              width: 150,
              textAlign: columnAlignments?.[colHeaders[0]] ?? "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>{colHeaders[0]}</span>
              {filterActivated && (
                <IconButton size="small" onClick={() => handleSort(colHeaders[0])}>
                  <SortIndicator
                    active={sortedColumn === colHeaders[0]}
                    direction={sortDirection}
                  />
                </IconButton>
              )}
            </div>
          </TableCell>
          <TableCell
            style={{
              ...headerCellStyle,
              width: "100%",
              textAlign: columnAlignments?.[colHeaders[1]] ?? "left",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: columnAlignments?.[colHeaders[1]] ?? "left",
              }}
            >
              <span>{colHeaders[1]}</span>
              {filterActivated && (
                <IconButton size="small" onClick={() => handleSort(colHeaders[1])}>
                  <SortIndicator
                    active={sortedColumn === colHeaders[1]}
                    direction={sortDirection}
                  />
                </IconButton>
              )}
            </div>
          </TableCell>
          <TableCell style={{ ...headerCellStyle, width: 150, position: "relative" }}>
            {showSearchBar && (
              <div style={{ position: "absolute", right: 10, bottom: 10 }}>
                <TableSearchField
                  value={searchString}
                  onChange={handleSearch}
                  placeholder={`${t("general.languages.search")}...`}
                  width={220}
                />
              </div>
            )}
          </TableCell>
        </TableRow>
      </TableHead>
    </Table>
  );

  const renderBody = () => (
    <div
      style={{ overflowY: "auto", maxHeight: maxTableHeightPx - headerHeight }}
      className="personalize-scroll-visible"
    >
      <Table style={{ tableLayout: "fixed", width: "100%" }}>
        <TableBody>
          {rowsToRender.length > 0 ? (
            rowsToRender.map((row, index) => (
              <TableRow
                key={row.id || index}
                hover
                className="h-16"
                style={{
                  backgroundColor:
                    nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id)
                      ? "rgba(0,200,137,0.2)"
                      : undefined,
                }}
              >
                {checkAll && (
                  <TableCell
                    style={{
                      width: 50,
                      padding:
                        nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id)
                          ? "0 11px"
                          : "0 8px",
                      textAlign: "center",
                    }}
                  >
                    {nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id) ? (
                      <Checkbox
                        size="small"
                        color="default"
                        checked
                        disabled
                        icon={<LockClosedIcon className="w-4 h-4" />}
                        checkedIcon={<LockClosedIcon className="w-4 h-4" />}
                      />
                    ) : (
                      <Checkbox
                        size="small"
                        color="default"
                        sx={{
                          color: "gray",
                          "&.Mui-checked": { color: "#00C889" },
                        }}
                        checked={selectedRowIds.includes(row.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowCheckboxChange(row.id);
                        }}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell
                  style={{
                    width: 150,
                    padding: "0 8px",
                    textAlign: columnAlignments?.[colHeaders[0]] ?? "left",
                  }}
                  className={cellClassName}
                >
                  {customCellRenderers && customCellRenderers[colHeaders[0]]
                    ? customCellRenderers[colHeaders[0]](row)
                    : renderTableCellValue(row[colHeaders[0]])}
                </TableCell>
                <TableCell
                  style={{
                    width: "100%",
                    padding: "0 8px",
                    textAlign: columnAlignments?.[colHeaders[1]] ?? "left",
                  }}
                  className={cellClassName}
                >
                  {customCellRenderers && customCellRenderers[colHeaders[1]]
                    ? customCellRenderers[colHeaders[1]](row)
                    : renderTableCellValue(row[colHeaders[1]])}
                </TableCell>
                <TableCell style={{ width: 150, padding: "0 8px", textAlign: "right" }}>
                  {nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id) ? (
                    <LanguageIcon
                      height={20}
                      width={20}
                      stroke="rgb(0 200 137)"
                      style={{ marginLeft: "3rem" }}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={checkAll ? 4 : 3} align="center">
                {emptyPlaceholder}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <TableContainer
      component={Paper}
      ref={ref}
      className={className}
      style={{ maxHeight: maxTableHeightPx, overflow: "hidden" }}
    >
      {renderHeader()}
      {renderBody()}
    </TableContainer>
  );
}) as LanguageSelectionTableComponent;

LanguageSelectionTable.displayName = "LanguageSelectionTable";

export default LanguageSelectionTable;
